/**
 * Invoices: built per location from monthly rates, completed jobs and
 * delivered supplies, then sent through QuickBooks, which numbers them and
 * holds the payment link (docs/majik/DATA-ARCHITECTURE-SPEC.md → Billing).
 * Each line can point back at the job it bills, so nothing is billed twice. Pure.
 */
import { z } from 'zod'
import { paymentTerms, taxCodes } from '../clients/client.ts'
import { recordIdSchema } from '../shared/record-id.ts'

export const invoiceStatuses = [
  'draft',
  'ready_to_send',
  'needs_attention',
  'sent',
  'paid',
  'void',
  'uncollectible',
] as const
export type InvoiceStatus = (typeof invoiceStatuses)[number]

/** Void and Uncollectible end an invoice without a payment (D-033). */
export const closedStatuses = ['void', 'uncollectible'] as const
export const isClosed = (status: InvoiceStatus) => status === 'void' || status === 'uncollectible'

/** Where Edit status can take an invoice. `waiting` means back to sent, or to draft if it never went out. */
export type StatusMove = 'paid' | 'void' | 'uncollectible' | 'waiting'

/**
 * The moves that make sense from where the invoice is (Gideon, Oct 26):
 * an unsent one can only be voided; a sent or overdue one can be paid, voided
 * or written off; a paid one goes back to waiting; a void or uncollectible one
 * does too. Both the page and the server use this, so they never disagree.
 */
export function statusMoves(invoice: Pick<Invoice, 'status'>): StatusMove[] {
  switch (invoice.status) {
    case 'draft':
    case 'ready_to_send':
    case 'needs_attention':
      return ['void']
    case 'sent':
      return ['paid', 'void', 'uncollectible']
    case 'paid':
    case 'void':
    case 'uncollectible':
      return ['waiting']
  }
}

export const statusMoveLabels: Record<StatusMove, string> = {
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
  waiting: 'Back to waiting',
}

/** The status "back to waiting" lands on: sent if it ever went out, draft if it never did. */
export const reopenedStatus = (invoice: Pick<Invoice, 'lastSentAt'>): 'sent' | 'draft' =>
  invoice.lastSentAt ? 'sent' : 'draft'

/** How a payment came in, as Edit status → Paid asks it. Kept in the payment's Activity note. */
export const paymentMethods = ['check', 'ach', 'card', 'avidxchange', 'other'] as const
export type PaymentMethod = (typeof paymentMethods)[number]
export const paymentMethodLabels: Record<PaymentMethod, string> = {
  check: 'Check',
  ach: 'ACH',
  card: 'Card',
  avidxchange: 'AvidXchange',
  other: 'Other',
}

export const invoiceTypes = ['monthly', 'one_off'] as const

export const invoiceLineSchema = z.object({
  id: recordIdSchema,
  itemName: z.string().min(1),
  description: z.string().default(''),
  /** Negative for a credit. */
  unitPrice: z.number(),
  quantity: z.number().positive(),
  /** The job this line bills, when it bills one. */
  jobId: recordIdSchema.nullable().default(null),
  sortOrder: z.number().int(),
})
export type InvoiceLine = z.infer<typeof invoiceLineSchema>

export const invoiceSchema = z.object({
  id: recordIdSchema,
  /** "<location> | <Month>" or "<location> | MM-DD-YYYY". */
  name: z.string().min(1),
  clientId: recordIdSchema,
  locationId: recordIdSchema.nullable(),
  type: z.enum(invoiceTypes),
  /** The first of the month billed, for a monthly invoice. */
  billingMonth: z.iso.date().nullable().default(null),
  status: z.enum(invoiceStatuses),
  /**
   * The invoice's own date ('YYYY-MM-DD', business zone) — lists sort by it.
   * A new one is dated the day it is made; an imported one keeps Monday's.
   */
  issuedOn: z.iso.date(),
  taxCode: z.enum(taxCodes),
  paymentTerms: z.enum(paymentTerms),
  /** Including tax, as QuickBooks totals it. */
  quickbooksTotal: z.number().nullable().default(null),
  /** What is still owed; 0 means paid. Shows part-payments. */
  balanceDue: z.number().nullable().default(null),
  /** QuickBooks' number, e.g. 1026-638060. */
  invoiceNumber: z.string().nullable().default(null),
  dueDate: z.iso.date().nullable().default(null),
  lastSentAt: z.iso.datetime().nullable().default(null),
  paidAt: z.iso.datetime().nullable().default(null),
  /** When it was voided or written off; null otherwise. */
  closedAt: z.iso.datetime().nullable().default(null),
  /** Why it was voided or written off, in the person's own words; optional. */
  statusReason: z.string().nullable().default(null),
  /**
   * Imported from Monday as "Sent" and 31+ days old: it may well be paid in
   * QuickBooks already. No payment reminders and no overdue row on Today until
   * someone confirms it is really unpaid (docs/majik/DATA-MODEL-V1.md §11 q2).
   */
  importedUnpaidStale: z.boolean().default(false),
  lines: z.array(invoiceLineSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Invoice = z.infer<typeof invoiceSchema>

export function invoiceSubtotal(invoice: Pick<Invoice, 'lines'>): number {
  return Math.round(invoice.lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0) * 100) / 100
}

/** Imported as unpaid and not yet confirmed: reminders stay off, Today asks once for all of them. */
export const awaitsUnpaidCheck = (invoice: Pick<Invoice, 'status' | 'importedUnpaidStale'>) =>
  invoice.importedUnpaidStale && invoice.status === 'sent'

/** Sent, still owing, and sent longer ago than the business rules allow. */
export function isOverdue(
  invoice: Pick<Invoice, 'status' | 'lastSentAt' | 'balanceDue'> &
    Partial<Pick<Invoice, 'importedUnpaidStale'>>,
  now: string,
  overdueAfterDays: number,
): boolean {
  if (invoice.status !== 'sent' || !invoice.lastSentAt) return false
  // Imported as unpaid and not yet checked: it may be paid already, so never "late".
  if (invoice.importedUnpaidStale) return false
  if (invoice.balanceDue !== null && invoice.balanceDue <= 0) return false
  return Date.parse(now) - Date.parse(invoice.lastSentAt) > overdueAfterDays * 86_400_000
}
